import { Button, Card, useToast } from "@helmr/ui";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isApiError, newIdempotencyKey } from "../../api/client";
import { createLead } from "../../api/leads";
import { NoAccess } from "../../app/NoAccess";
import { useServices } from "../../app/services";
import { useInvalidateLeadData, useScope, useStaffNames } from "./hooks";
import { leads as t } from "./strings";
import { leadName } from "./ui";
import s from "./leads.module.css";

const SOURCES = ["manual", "walk_in", "whatsapp", "website"] as const;

export function AddLeadPage() {
  const sc = useScope();
  const { client } = useServices();
  const nav = useNavigate();
  const toast = useToast();
  const staff = useStaffNames(sc);
  const refresh = useInvalidateLeadData();
  const branches = sc.ctx.me?.branches ?? [];
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [source, setSource] = useState<string>("walk_in");
  const [branchId, setBranchId] = useState(sc.branchId ?? sc.ctx.me?.branch?.id ?? "");
  const [ownerId, setOwnerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per intent: a retried submit is the same request.
  const key = useRef(newIdempotencyKey());

  if (sc.ctx.status !== "loading" && !sc.ctx.has("leads.add")) return <NoAccess />;
  const branch = branchId || sc.ctx.me?.branch?.id || branches[0]?.id || "";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!phone.trim()) { setError(t.new.phoneRequired); return; }
    if (!branch) { setError(t.new.branchRequired); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await createLead(client, { phone: phone.trim(), ...(name.trim() ? { name: name.trim() } : {}), ...(email.trim() ? { email: email.trim() } : {}), source, branchId: branch, ...(ownerId ? { ownerId } : {}) }, key.current);
      key.current = newIdempotencyKey();
      await refresh();
      if (r.action !== "created") toast.show(t.new.existing(leadName(r.lead)));
      nav(`/leads/${r.lead.id}`);
    } catch (err) {
      setError(isApiError(err) ? err.message : t.new.failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={s.page}>
      <title>Add lead · Helmr</title>
      <div className={s.head}>
        <div>
          <Link to="/leads" className={s.back}>← {t.profile.back}</Link>
          <h1 tabIndex={-1}>{t.new.title}</h1>
          <p>{t.new.sub}</p>
        </div>
      </div>
      <Card>
        <div className={s.formCard}>
          <form className={s.form} onSubmit={submit} noValidate>
            <div className={s.field}><label htmlFor="nl-name">{t.new.name}</label><input id="nl-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" /></div>
            <div className={s.field}>
              <label htmlFor="nl-phone">{t.new.phone}</label>
              <input id="nl-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} autoComplete="off" aria-describedby="nl-phone-hint" required />
              <small id="nl-phone-hint">{t.new.phoneHint}</small>
            </div>
            <div className={s.field}><label htmlFor="nl-email">{t.new.email}</label><input id="nl-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} autoComplete="off" /></div>
            <div className={s.field}>
              <label htmlFor="nl-source">{t.new.source}</label>
              <select id="nl-source" value={source} onChange={(e) => setSource(e.target.value)}>{SOURCES.map((k) => <option key={k} value={k}>{t.sources[k]}</option>)}</select>
            </div>
            {branches.length > 1 ? (
              <div className={s.field}>
                <label htmlFor="nl-branch">{t.new.branch}</label>
                <select id="nl-branch" value={branch} onChange={(e) => setBranchId(e.target.value)}>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select>
              </div>
            ) : null}
            {staff.canList ? (
              <div className={s.field}>
                <label htmlFor="nl-owner">{t.new.owner}</label>
                <select id="nl-owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                  <option value="">{t.you}</option>
                  {staff.list.filter((p) => p.id !== sc.ctx.me?.staff.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            ) : null}
            {error ? <p className={s.err} role="alert">{error}</p> : null}
            <div className={s.formActions}>
              <Button variant="primary" type="submit" busy={busy} busyLabel={t.new.saving}>{t.new.save}</Button>
              <Button as={Link} to="/leads">{t.new.cancel}</Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
